/**
 * Leased Fee Valuation
 *
 * Computes the value of a leased QSR (or similar) tenant's facility lease as a
 * separate real-estate asset using the income capitalization approach. This
 * runs on the frontend so the regressor only needs the small "stop adding
 * QSR rent to gross profit" change. The value is added on top of the
 * regressor's going-concern output to produce the total asset value.
 *
 * Methodology:
 *   annualRent      = monthlyRent × 12
 *   NOI             = annualRent × (1 − vacancyAndLossRate)
 *   appliedCapRate  = base cap rate ± tenant tier ± term ± HCOL,
 *                     bounded by [floor, ceiling]
 *   leasedFeeValue  = NOI ÷ appliedCapRate
 *
 * Tunables (v1, agreed):
 *   base                 = 750 bp
 *   tier A / B / C       = -25 / 0 / +25 bp
 *   term <5 / 5-9 / 10-14 / 15-19 / 20+ years = +50 / +25 / 0 / -25 / -50 bp
 *   HCOL zip             = -25 bp
 *   bounds               = 600 / 900 bp
 *   vacancy & loss       = 5% flat
 */

import { HCOL_ZIPS } from './hcolZips';

export type GuarantyType = 'corporate' | 'franchisee' | 'unsure';
export type BrandTier = 'A' | 'B' | 'C';

const BASE_CAP_RATE_BPS = 750;
const HCOL_ADJUSTMENT_BPS = -25;
const CAP_RATE_FLOOR_BPS = 600;
const CAP_RATE_CEILING_BPS = 900;
const DEFAULT_VACANCY_AND_LOSS = 0.05;

const TIER_ADJUSTMENT_BPS: Record<BrandTier, number> = {
  A: -25,
  B: 0,
  C: 25,
};

/**
 * Default tier per brand. Guaranty type can demote (or in the unsure case,
 * leave alone) a Tier-A brand to Tier B; Tier B and C are unaffected by
 * guaranty type because their credit is already operator-driven.
 */
const BRAND_TIER_DEFAULTS: Record<string, BrandTier> = {
  "McDonald's": 'A',
  'Starbucks': 'A',
  'Chick-fil-A': 'A',
  'Chipotle': 'A',
  'Subway': 'B',
  'Jack in the Box': 'B',
  "Carl's Jr": 'B',
  "Wendy's": 'B',
  "Burger King": 'B',
  'Taco Bell': 'B',
  'KFC': 'B',
  "Local/Independent": 'C',
};

export interface CapRateAdjustment {
  factor: 'base' | 'tenantTier' | 'term' | 'hcol' | 'floor' | 'ceiling';
  basisPoints: number;
  reason: string;
}

export interface CapRateResult {
  baseBps: number;
  adjustments: CapRateAdjustment[];
  appliedBps: number;
  appliedRate: number;
  tenantTier: BrandTier;
  hcol: boolean;
}

export interface CapRateInput {
  tenantName?: string;
  guarantyType?: GuarantyType;
  remainingTermYears: number;
  zipCode?: string;
}

export interface LeasedFeeInput extends CapRateInput {
  monthlyRent: number;
  vacancyAndLossRate?: number;
}

export interface LeasedFeeResult {
  monthlyRent: number;
  annualRent: number;
  vacancyAndLossRate: number;
  vacancyAndLossAmount: number;
  noi: number;
  capRate: CapRateResult;
  leasedFeeValue: number;
}

export function resolveTier(tenantName: string | undefined, guaranty: GuarantyType | undefined): BrandTier {
  if (!tenantName) return 'C';
  const defaultTier = BRAND_TIER_DEFAULTS[tenantName] ?? 'C';
  if (defaultTier === 'A' && guaranty === 'franchisee') return 'B';
  return defaultTier;
}

function termAdjustmentBps(remainingYears: number): { bps: number; bucket: string } {
  if (remainingYears < 5) return { bps: 50, bucket: '< 5 years remaining' };
  if (remainingYears < 10) return { bps: 25, bucket: '5–9 years remaining' };
  if (remainingYears < 15) return { bps: 0, bucket: '10–14 years remaining' };
  if (remainingYears < 20) return { bps: -25, bucket: '15–19 years remaining' };
  return { bps: -50, bucket: '20+ years remaining' };
}

function isHcolZip(zip: string | undefined): boolean {
  if (!zip) return false;
  const cleaned = zip.toString().trim().slice(0, 5);
  return HCOL_ZIPS.has(cleaned);
}

export function selectCapRate(input: CapRateInput): CapRateResult {
  const tier = resolveTier(input.tenantName, input.guarantyType);
  const hcol = isHcolZip(input.zipCode);
  const term = termAdjustmentBps(input.remainingTermYears);

  const adjustments: CapRateAdjustment[] = [
    { factor: 'base', basisPoints: BASE_CAP_RATE_BPS, reason: `Base cap rate (${BASE_CAP_RATE_BPS} bp)` },
    {
      factor: 'tenantTier',
      basisPoints: TIER_ADJUSTMENT_BPS[tier],
      reason: `Tenant tier ${tier} (${input.tenantName || 'no tenant'}${
        input.guarantyType ? `, ${input.guarantyType} guaranty` : ''
      })`,
    },
    {
      factor: 'term',
      basisPoints: term.bps,
      reason: `Lease term: ${term.bucket}`,
    },
  ];
  if (hcol) {
    adjustments.push({
      factor: 'hcol',
      basisPoints: HCOL_ADJUSTMENT_BPS,
      reason: `High-cost-of-living zip (${input.zipCode})`,
    });
  }

  const sumBps = adjustments.reduce((acc, a) => acc + a.basisPoints, 0);
  let finalBps = sumBps;

  if (finalBps < CAP_RATE_FLOOR_BPS) {
    adjustments.push({
      factor: 'floor',
      basisPoints: CAP_RATE_FLOOR_BPS - finalBps,
      reason: `Floor applied (${CAP_RATE_FLOOR_BPS} bp minimum)`,
    });
    finalBps = CAP_RATE_FLOOR_BPS;
  } else if (finalBps > CAP_RATE_CEILING_BPS) {
    adjustments.push({
      factor: 'ceiling',
      basisPoints: CAP_RATE_CEILING_BPS - finalBps,
      reason: `Ceiling applied (${CAP_RATE_CEILING_BPS} bp maximum)`,
    });
    finalBps = CAP_RATE_CEILING_BPS;
  }

  return {
    baseBps: BASE_CAP_RATE_BPS,
    adjustments,
    appliedBps: finalBps,
    appliedRate: finalBps / 10000,
    tenantTier: tier,
    hcol,
  };
}

export function computeLeasedFeeValue(input: LeasedFeeInput): LeasedFeeResult {
  const monthlyRent = Math.max(0, input.monthlyRent || 0);
  const annualRent = monthlyRent * 12;
  const vacancyAndLossRate = input.vacancyAndLossRate ?? DEFAULT_VACANCY_AND_LOSS;
  const vacancyAndLossAmount = annualRent * vacancyAndLossRate;
  const noi = annualRent - vacancyAndLossAmount;
  const capRate = selectCapRate(input);
  const leasedFeeValue = capRate.appliedRate > 0 ? noi / capRate.appliedRate : 0;

  return {
    monthlyRent,
    annualRent,
    vacancyAndLossRate,
    vacancyAndLossAmount,
    noi,
    capRate,
    leasedFeeValue,
  };
}

/**
 * Convert a 5-digit zip from a free-form address string. Returns the trailing
 * 5 digits if they appear in the address, otherwise undefined. The frontend
 * captures location as e.g. "12078 THREE FLAGS CT, OAK HILLS, California 92344".
 */
export function extractZipFromLocation(location: string | undefined): string | undefined {
  if (!location) return undefined;
  const match = location.match(/(\b\d{5})(?:-\d{4})?\b\s*$/);
  return match ? match[1] : undefined;
}

/**
 * Compute remaining lease term in whole years. MTM treated as 0 (worst-case
 * rollover risk). Negative or absent end years also treated as 0.
 */
export function computeRemainingTermYears(input: {
  qsrIsMonthToMonth?: boolean;
  qsrLeaseEndYear?: number;
  asOfYear?: number;
}): number {
  if (input.qsrIsMonthToMonth) return 0;
  const end = input.qsrLeaseEndYear;
  if (!end || end <= 0) return 0;
  const asOf = input.asOfYear ?? new Date().getFullYear();
  return Math.max(0, end - asOf);
}
